// Tracks every in-flight axios request so logout can abort them first.
//
// The API's cookie session driver keeps the session in an encrypted cookie, so
// the last response to land wins. A slow request still running when the user
// logs out (e.g. GET /projects/mine on the dashboard) can land after the
// logout response and re-set both the session cookie and the auth flag
// cookie, and the hard reload to the login page then finds the user logged
// back in. Aborting a request makes the browser discard its response,
// Set-Cookie headers included.
//
// Requests that already carry their own `signal` are left alone: their caller
// owns cancellation.

const inflight = new Set();

// Symbol key so the controller rides along on the request config without
// colliding with (or being serialized as) an axios option.
const CONTROLLER = Symbol("inflightController");

function forget(config) {
	const controller = config?.[CONTROLLER];
	if (controller) {
		inflight.delete(controller);
	}
}

/**
 * Install the tracking interceptors on an axios instance. Call once at
 * startup, before any request is made.
 *
 * @param {import("axios").AxiosInstance} http
 */
export function installInflightTracking(http) {
	http.interceptors.request.use((config) => {
		if (!config.signal) {
			const controller = new AbortController();
			config.signal = controller.signal;
			config[CONTROLLER] = controller;
			inflight.add(controller);
		}
		return config;
	});
	http.interceptors.response.use(
		(response) => {
			forget(response.config);
			return response;
		},
		(error) => {
			forget(error?.config);
			return Promise.reject(error);
		},
	);
}

/**
 * Abort every tracked request that has not settled yet. Aborted requests
 * reject with axios's CanceledError (`axios.isCancel(error)` is true).
 */
export function abortAllInflight() {
	for (const controller of inflight) {
		controller.abort();
	}
	inflight.clear();
}

/** Number of tracked requests still in flight (for tests). */
export function inflightCount() {
	return inflight.size;
}
